<template>
	<div class="box">
		<LoadingSpinner v-if="loading" />
		<div v-else>
			<p>{{ $t('compta.fec.intro') }}</p>

			<!-- Le contrôle vient du serveur : c'est lui qui connaît les règles du format, le front
			     n'a pas à les dupliquer. -->
			<b-alert v-if="!can_export" variant="danger" show class="mt-2">
				{{ $t('compta.fec.cant_export') }}
				<div v-for="check in checks" :key="check.code" class="mt-2">
					<strong>{{ $t('compta.fec.check.' + check.code) }}</strong>
					<template v-if="check.items.length && !isFixable(check)"> : {{ check.items.map(i => i.label).join(', ') }}</template>

					<!-- Comptes clients réparables : chacun s'ouvre pour correction manuelle, ou se
					     renumérote depuis le gabarit du plan comptable. -->
					<template v-if="isFixable(check)">
						<b-button variant="outline-primary" size="sm" class="ml-2" :disabled="fixing !== null" @click.prevent="fix(null)">
							<font-awesome-icon :icon="['fal', 'sync-alt']" /> {{ $t('compta.fec.fix_all', {count: check.items.length}) }}
						</b-button>
						<ul class="fec-invalid-accounts">
							<li v-for="item in check.items" :key="item.account">
								<router-link :to="{ name: 'tiersFiche', params: { tiers_id: item.tiers }}">{{ item.label }}</router-link>
								<b-button variant="link" size="sm" :disabled="fixing !== null" :title="$t('compta.fec.fix_one')" @click.prevent="fix([item.account])">
									<font-awesome-icon :icon="['fal', 'sync-alt']" :spin="fixing === item.account" />
								</b-button>
							</li>
						</ul>
					</template>
				</div>
			</b-alert>

			<!-- Génération en cours : l'avancement se lit dans les pièces déjà traitées -->
			<div v-else-if="generating">
				<p><font-awesome-icon :icon="['fas', 'spinner']" pulse /> {{ $t('compta.fec.generating') }}</p>
				<template v-if="progress">
					<label>{{ $t('compta.fec.progress_invoices', {done: progress.invoices_done, total: progress.invoices_total}) }}</label>
					<b-progress :value="progress.invoices_done" :max="progress.invoices_total || 1" class="mb-2" />
					<label>{{ $t('compta.fec.progress_payments', {done: progress.payments_done, total: progress.payments_total}) }}</label>
					<b-progress :value="progress.payments_done" :max="progress.payments_total || 1" class="mb-2" />
					<label>{{ $t('compta.fec.progress_purchases', {done: progress.purchases_done, total: progress.purchases_total}) }}</label>
					<b-progress :value="progress.purchases_done" :max="progress.purchases_total || 1" />
				</template>
			</div>

			<div v-else class="form-group">
				<!-- La date de départ ne se choisit qu'une fois : elle fige la profondeur d'historique
				     reprise, que les générations suivantes relisent sur le premier export. Ensuite il
				     n'y a plus rien à choisir, le delta détermine seul ce qui sort. -->
				<template v-if="initialising">
					<label for="fec_start">{{ $t('compta.fec.start') }}</label>
					<e-select
						id="fec_start"
						track-by="value"
						label="label"
						v-model="start"
						:options="starts"
						:searchable="false"
						:allow-empty="false"
						:show-labels="false"
					/>
					<small class="d-block text-muted mt-1">{{ $t('compta.fec.start_help') }}</small>
				</template>
				<div class="text-center mt-3">
					<button class="btn btn-primary" type="button" :disabled="processing" @click.prevent="launch">
						<font-awesome-icon v-if="processing" :icon="['fas', 'spinner']" pulse />{{ initialising ? $t('compta.fec.generate') : $t('compta.fec.generate_next') }}
					</button>
				</div>
			</div>

			<b-alert v-if="empty_delta" variant="info" show class="mt-2">{{ $t('compta.fec.nothing_new') }}</b-alert>

			<!-- Un compte bloqué n'est pas un échec de la renumérotation : c'est un motif de gabarit
			     incapable de produire des numéros uniques, et l'utilisateur doit savoir quoi changer. -->
			<b-alert v-if="fix_blocked" variant="warning" show class="mt-2">
				{{ $t('compta.fec.fix_blocked', {count: fix_blocked}) }}
			</b-alert>

			<!-- Onboarding : tant que le rapprochement des comptes n'est pas au point, il faut pouvoir
			     tout refaire. Réservé à l'administrateur de la licence, comme les autres réglages
			     comptables sensibles. -->
			<div v-if="is_admin && !generating && server_total_rows > 0" class="mt-4">
				<b-button variant="outline-danger" size="sm" :disabled="resetting" @click.prevent="confirmReset">
					<font-awesome-icon v-if="resetting" :icon="['fas', 'spinner']" pulse />{{ $t('compta.fec.reset') }}
				</b-button>
				<small class="d-block text-muted mt-1">{{ $t('compta.fec.reset_help') }}</small>
			</div>

			<h3 class="mt-4">{{ $t('compta.fec.history') }}</h3>
			<CustomTable
				ref="table"
				id_table="fec_export"
				primaryKey="fecexport_id"
				:busy.sync="table_busy"
				:items="exports"
				:onlineMode="true"
				:serverTotalRows="server_total_rows"
				:externSlotColumns="['fecexport_generated_at', 'exercice', 'fecexport_start', 'fecexport_end', 'download']"
				@onTableParamsChange="onTableParamsChange"
			>
				<!-- Avec l'heure : plusieurs fichiers d'une même journée ne se distinguent pas autrement. -->
				<template v-slot:[`custom-slot-cell(fecexport_generated_at)`]="{ data }">{{ formatDate(data.fecexport_generated_at, true) }}</template>
				<!-- Une génération à cheval sur une clôture produit un fichier par exercice : c'est
				     l'exercice qui les distingue, la période demandée étant la même. -->
				<template v-slot:[`custom-slot-cell(exercice)`]="{ data }">
					{{ data.exercice ? $t('compta.fec.exercice_period', { exercice: data.exercice, start: formatDate(data.exercice_start), end: formatDate(data.exercice_end) }) : '' }}
				</template>
				<template v-slot:[`custom-slot-cell(fecexport_start)`]="{ data }">{{ data.fecexport_start ? formatDate(data.fecexport_start) : '' }}</template>
				<template v-slot:[`custom-slot-cell(fecexport_end)`]="{ data }">{{ formatDate(data.fecexport_end) }}</template>
				<template v-slot:[`custom-slot-cell(download)`]="{ data }">
					<button class="btn btn-secondary btn-sm" type="button" :disabled="downloading === data.fecexport_id" @click.prevent="download(data)">
						<font-awesome-icon :icon="['fas', 'download']" />
					</button>
				</template>
			</CustomTable>
		</div>

		<ModalConfirm
			ref="reset_modal"
			:text_title="$t('compta.fec.reset_title')"
			:text_question="$t('compta.fec.reset_question')"
			:text_message="$t('compta.fec.reset_message')"
			:text_button_ok="$t('compta.fec.reset')"
			:text_button_cancel="$t('modal.general.annuler')"
			:icon="['fal', 'trash-alt']"
			:callback_validation="reset"
		/>
	</div>
</template>

<script type="text/javascript">
	import Accounting from "@/mixins/Accounting.js"
	import Common from '@/assets/js/common.js'

	export default {
		name: 'AccountingFec',

		mixins: [Accounting],
		props: ['accountingplan_id'],
		data () {
			return {
				accounting: null,
				exports: [],
				table_busy: false,
				server_total_rows: -1,
				table_params: null,
				last_export: null,
				checks: [],
				fixing: null,
				fix_blocked: 0,
				loading: true,
				processing: false,
				generating: false,
				progress: null,
				empty_delta: false,
				start: null,
				downloading: null,
				resetting: false,
				poll_interval: null,
			}
		},

		mounted() {
			this.init_component()
		},

		beforeDestroy() {
			this.stopPolling()
		},

		methods: {
			async init_component() {
				this.loading = true
				this.accounting = await this.loadAccountingPlan(this.accountingplan_id)
				this.start = this.starts[this.starts.length - 1]
				await this.refresh()
				this.loading = false

				if(this.generating) {
					this.startPolling()
				}
			},

			/**
			 * Sérialise une date en AAAA-MM-JJ à partir de ses composantes locales.
			 *
			 * toISOString() ne conviendrait pas : il bascule en UTC et décale donc d'un jour toute date
			 * construite à minuit sur un fuseau en avance sur Greenwich — une fin de mois deviendrait
			 * l'avant-dernier jour.
			 * @param  {Date} date
			 * @return {String}
			 */
			toIsoDate(date) {
				const month = ('0' + (date.getMonth() + 1)).slice(-2)
				const day = ('0' + date.getDate()).slice(-2)

				return date.getFullYear() + '-' + month + '-' + day
			},

			async refresh() {
				const status = await this.getFecStatus(this.accountingplan_id)

				if(!status) return false

				const was_generating = this.generating

				this.generating = status.generating
				this.progress = status.progress
				this.last_export = status.last_export
				this.checks = status.checks

				// La génération vient de se terminer : si aucun fichier n'en est sorti, c'est que rien
				// n'avait bougé depuis la dernière livraison.
				if(was_generating && !this.generating) {
					this.empty_delta = !this.last_export || !this.isRecent(this.last_export)

					await this.refreshTable()
				}
			},

			/**
			 * Un export produit à l'instant, par opposition à celui d'une livraison précédente restée
			 * en tête de liste parce que la génération n'a rien trouvé de neuf.
			 */
			isRecent(fec_export) {
				return (new Date() - new Date(fec_export.fecexport_generated_at)) < 120000
			},

			/**
			 * Recharge la page courante du tableau. Le tableau rend lui-même ses paramètres de vue à sa
			 * montée : rien n'est chargé d'ici tant qu'il ne les a pas donnés.
			 */
			async loadExports(table_params = null) {
				this.table_busy = true

				const result = await this.loadFecExportsOnline(this.accountingplan_id, table_params ? JSON.parse(JSON.stringify(table_params)) : null)

				this.exports = result ? result.data : []
				this.server_total_rows = result ? result.total : -1

				this.table_busy = false
			},

			onTableParamsChange(table_params) {
				this.table_params = table_params
				this.loadExports(table_params)
			},

			async refreshTable() {
				if(this.table_params) {
					await this.loadExports(this.table_params)
				}

				this.$refs.table.refreshTable()
			},

			async launch() {
				if(this.processing) return false
				this.processing = true
				this.empty_delta = false

				await this.exportFec(this.accountingplan_id, this.initialising ? this.start.value : null)

				this.generating = true
				this.processing = false
				this.startPolling()
			},

			async download(fec_export) {
				if(this.downloading) return false
				this.downloading = fec_export.fecexport_id

				const result = await this.downloadFec(this.accountingplan_id, fec_export.fecexport_id)

				if(result) {
					Common.csvToFile(result.content, result.filename)
				}

				this.downloading = null
			},

			confirmReset() {
				this.$refs.reset_modal.openModal()
			},

			async reset() {
				if(this.resetting) return false
				this.resetting = true

				await this.resetFec(this.accountingplan_id)

				this.$refs.reset_modal.closeModal()
				this.empty_delta = false
				await this.refresh()
				this.resetting = false
			},

			/**
			 * Seuls les comptes clients se réparent tout seuls, et à condition que le gabarit de
			 * numérotation du plan soit lui-même conforme — sinon le contrôle remonte
			 * invalid_client_account_template et il n'y a rien à régénérer.
			 */
			isFixable(check) {
				return check.code === 'invalid_client_account'
			},

			async fix(account_ids) {
				if(this.fixing !== null) return false
				this.fixing = account_ids ? account_ids[0] : 'all'
				this.fix_blocked = 0

				const result = await this.fixFecClientAccounts(this.accountingplan_id, account_ids)

				if(result) {
					this.fix_blocked = result.blocked
				}

				await this.refresh()

				this.fixing = null
			},

			startPolling() {
				this.stopPolling()
				this.poll_interval = setInterval(async () => {
					await this.refresh()

					if(!this.generating) {
						this.stopPolling()
					}
				}, 3000)
			},

			stopPolling() {
				if(this.poll_interval) {
					clearInterval(this.poll_interval)
					this.poll_interval = null
				}
			}
		},

		computed: {
			/**
			 * Une comptabilité qui n'a encore rien livré : c'est le seul moment où la date de départ
			 * se choisit, puisqu'elle est ensuite figée sur le premier export.
			 */
			initialising() {
				return this.server_total_rows === 0
			},

			/**
			 * Départs proposés : les débuts de mois de l'année écoulée.
			 *
			 * La liste s'arrête volontairement à douze mois. Reprendre tout l'historique produirait un
			 * premier fichier de plusieurs exercices, que l'expert-comptable n'a pas à réintégrer, et
			 * proposer une date plus ancienne laisserait croire qu'elle serait couverte.
			 */
			starts() {
				const options = []
				const today = new Date()

				for (let i = 0; i < 12; i++) {
					const first_of_month = new Date(today.getFullYear(), today.getMonth() - i, 1)
					options.push({
						value: this.toIsoDate(first_of_month),
						label: first_of_month.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
					})
				}

				return options
			},

			is_admin() {
				return this.$store.state.userAccess.hasLicenceAdminAccess
			},

			can_export() {
				return this.checks.length === 0
			}
		},

		components: {
			CustomTable: () => import('GroomyRoot/components/Table/CustomTable'),
			LoadingSpinner: () => import('GroomyRoot/components/Logos/LoadingSpinner_35'),
			ModalConfirm: () => import('GroomyRoot/components/Modals/ModalConfirm')
		}
	}
</script>
